import React, { useState } from 'react';
import {
  Box,
  Grid,
  Pagination,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';

import NavBar from 'components/navbar';

import BlogCard from './BlogCard';

import { LandingFooter } from 'layouts/LandingPage/LandingFooter';

import { useLazyListBlogsQuery, useGetBlogCategoriesQuery } from 'store/services/publicApi';
import { CategorySharp, Close } from "@mui/icons-material";

const BlogListScreen = () => {

  const theme = useTheme();

  const postsPerPage = 6;

  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');

  const [selectedCategories, setCategories] = useState([]);

  const [blogs, setBlogs] = useState([]);

  const [pageCount, setPageCount] = useState(0);

  // Fetch data hooks
  const [fetchBlogs, { data, isLoading }] = useLazyListBlogsQuery();

  const { data: categoriesData } = useGetBlogCategoriesQuery();

  // Get all available categories
  const categories = categoriesData?.map(cat => cat.name) || [];

  // Fetch blogs when filters change
  React.useEffect(() => {
    const categoryParam = selectedCategories.join(',');
    fetchBlogs({
      active: 'true',
      keyword: searchTerm,
      page: currentPage,
      limit: postsPerPage,
      ...(categoryParam && { category: categoryParam }),
    });
  }, [fetchBlogs, searchTerm, selectedCategories, currentPage]);

  // Update blog data and pagination
  React.useEffect(() => {
    if (data) {
      setBlogs(data.data);
      setPageCount(Math.ceil(data.totalItems / postsPerPage));
    }
  }, [data]);

  // Reset to page 1 on filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategories]);

  const clearSearchText = () => {
    setSearchTerm("")
  }

  return (
    <>
      <NavBar />
      <Box
        sx={{
          minHeight: '61vh',
          bgcolor: 'background.default',
          pt: 10,
          px: { xs: 2, md: 4 },
          pb: 6,
          background:
            theme.palette.mode === 'dark'
              ? 'radial-gradient(circle at center, #1a1a1a, #000)'
              : 'linear-gradient(45deg, #f8f8f8 0%, #ffffff 100%)',
        }}
      >
        {/* Title + Search */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 4,
                  bgcolor: '#dc143c',
                  borderRadius: 2,
                },
              }}
            >
              Latest Articles
            </Typography>

            <TextField
              placeholder="Search blogs..."
              variant="outlined"
              size="medium"
              label="Title/Content"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{
                width: { xs: '100%', md: 900 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 5,
                  bgcolor: 'background.paper',
                  boxShadow: theme.shadows[1],
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearchText} edge="end">
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar: Category Filters */}
          <Grid item xs={12} md={2.1}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'crimson',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} 
            >
              <CategorySharp sx={{ fontSize: 22 }} />
              Filter by Category
            </Typography>
            <Stack spacing={1}>
              <Grid container spacing={1}>
                {categories.map((cat, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={selectedCategories.includes(cat)}
                        onChange={() =>
                          setCategories(prev =>
                            prev.includes(cat)
                              ? prev.filter(x => x !== cat)
                              : [...prev, cat]
                          )
                        }
                        sx={{
                          '&.Mui-checked': {
                            color: 'crimson',
                          },
                        }}
                      />
                    }
                    label={
                      <Chip
                        label={cat}
                        sx={{
                          width: '100%',
                          bgcolor: selectedCategories.includes(cat)
                            ? 'crimson'
                            : 'action.hover',
                          color: selectedCategories.includes(cat)
                            ? 'primary.contrastText'
                            : 'text.primary',
                        }}
                      />
                    }
                    sx={{ m: 0 }}
                  />
                ))}
              </Grid>
            </Stack>
          </Grid>

          {/* Main: Blog Cards */}
          <Grid item xs={12} md={9.9}>
            {isLoading && (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <CircularProgress />
              </Box>
            )}

            {!isLoading && blogs.length === 0 && (
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No articles found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your search or filters
                </Typography>
              </Box>
            )}

            <Grid container spacing={4}>
              {blogs.map(post => (
                <Grid item key={post._id} xs={12} sm={6} lg={4}>
                  <BlogCard post={post} />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6, gap: 2 }}>
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(_, v) => setCurrentPage(v)}
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontWeight: 600,
                      '&.Mui-selected': {
                        bgcolor: 'crimson',
                        color: 'white',
                        '&:hover': { bgcolor: '#b01030' },
                      },
                    },
                  }}
                  slots={{
                    previous: () => <span style={{ fontWeight: 600 }}>Previous</span>,
                    next: () => <span style={{ fontWeight: 600 }}>Next</span>,
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Page {currentPage} of {pageCount} • Showing {blogs.length} results • Total {data?.totalItems || 0} Blogs
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <LandingFooter />
    </>
  );
};

export default BlogListScreen;

